import React from 'react';
import { Box, keyframes } from '@mui/material';
import { motion } from 'framer-motion';

const pulse = keyframes`
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.8;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
`;

const rotate = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

interface CustomLoaderProps {
  size?: number;
  message?: string;
}

const CustomLoader: React.FC<CustomLoaderProps> = ({ 
  size = 80, 
  message = 'LOADING' 
}) => {
  const radius = size * 0.7;
  const circumference = 2 * Math.PI * radius;
  const messageLength = message.length;
  const anglePerChar = 360 / messageLength;

  return (
    <Box
      sx={{
        position: 'relative',
        width: size * 2,
        height: size * 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {/* Pulsing Logo */}
      <Box
        component="img"
        src="/logo192.png"
        alt="Loading"
        sx={{
          width: size,
          height: size,
          objectFit: 'contain',
          animation: `${pulse} 2s ease-in-out infinite`,
          position: 'absolute',
          zIndex: 2,
          filter: 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.1))',
        }}
      />

      {/* Rotating Text Container */}
      <Box
        sx={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          animation: `${rotate} 8s linear infinite`,
          zIndex: 1,
        }}
      >
        {/* Circular Text */}
        <svg
          width={size * 2}
          height={size * 2}
          viewBox={`0 0 ${size * 2} ${size * 2}`}
          style={{ position: 'absolute', top: 0, left: 0 }}
        >
          <defs>
            <path
              id="circle"
              d={`
                M ${size}, ${size}
                m -${radius}, 0
                a ${radius},${radius} 0 1,1 ${radius * 2},0
                a ${radius},${radius} 0 1,1 -${radius * 2},0
              `}
            />
          </defs>
          <text
            fill="#1E293B"
            fontSize={size * 0.15}
            fontWeight="600"
            letterSpacing="0.3em"
            textAnchor="middle"
          >
            <textPath href="#circle" startOffset="50%">
              {message.split('').map((char, i) => (
                <tspan key={i} dy="0">
                  {char}
                </tspan>
              ))}
            </textPath>
          </text>
        </svg>
      </Box>

      {/* Animated Dots */}
      <Box
        sx={{
          position: 'absolute',
          bottom: -30,
          display: 'flex',
          gap: 1,
        }}
      >
        {[0, 1, 2].map((i) => (
          <motion.div
            key={i}
            animate={{
              y: [0, -8, 0],
            }}
            transition={{
              duration: 0.6,
              repeat: Infinity,
              delay: i * 0.2,
              ease: "easeInOut",
            }}
          >
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                bgcolor: 'primary.main',
                opacity: 0.6,
              }}
            />
          </motion.div>
        ))}
      </Box>
    </Box>
  );
};

export default CustomLoader;